import { useInitialLoading } from '../hooks/useInitialLoading';
import React, { useState, useEffect, useCallback } from 'react';
import { getAuthHeaders } from '../utils/authHeaders';
import { API, tableStyle, thStyle, tdStyle, btnSmall, btnPrimary, btnSecondary, overlayStyle, modalStyle } from './RpShared';

export default function RpPositions() {
  const companyId = localStorage.getItem('selectedCompanyId');
  const [positions, setPositions] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);
  const [editing, setEditing] = useState(null); // position object
  const [checked, setChecked] = useState(new Set());
  const [saving, setSaving] = useState(false);

  const fetchPositions = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/companies/${companyId}/roles-permissions/positions`, { headers: getAuthHeaders() });
      setPositions(await res.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [companyId]);

  const fetchCatalog = useCallback(async () => {
    try {
      const res = await fetch(`${API}/roles-permissions/catalog`, { headers: getAuthHeaders() });
      setCatalog(await res.json());
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { fetchPositions(); fetchCatalog(); }, [fetchPositions, fetchCatalog]);

  const openEditor = async (position) => {
    setEditing(position);
    setChecked(new Set());
    try {
      const res = await fetch(`${API}/companies/${companyId}/roles-permissions/positions/${position.id}`, { headers: getAuthHeaders() });
      const data = await res.json();
      setChecked(new Set(data.permission_ids || []));
    } catch (e) { console.error(e); }
  };

  const toggle = (id) => {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const savePreset = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/companies/${companyId}/roles-permissions/positions/${editing.id}/preset`, {
        method: 'PUT',
        headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ permission_ids: Array.from(checked) }),
      });
      if (!res.ok) throw new Error('Failed');
      setEditing(null);
      fetchPositions();
    } catch (e) { console.error(e); alert('Failed to save preset'); }
    setSaving(false);
  };

  if (initialLoading) return <div style={{ padding: 32, textAlign: 'center' }}>Loading...</div>;

  return (
    <div style={{ padding: '24px 32px', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      <h2 style={{ margin: '0 0 20px', fontSize: 22, fontWeight: 700, color: '#1a1a2e' }}>🧭 Positions & Permission Presets</h2>

      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: 18 }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Position</th>
                <th style={thStyle}>Department</th>
                <th style={thStyle}>Employees</th>
                <th style={thStyle}>Preset Permissions</th>
                <th style={thStyle}></th>
              </tr>
            </thead>
            <tbody>
              {positions.map((p) => (
                <tr key={p.id}>
                  <td style={tdStyle}>{p.title}</td>
                  <td style={tdStyle}>{p.department?.name || '—'}</td>
                  <td style={tdStyle}>{p.employees_count}</td>
                  <td style={tdStyle}>{p.preset_permission_count}</td>
                  <td style={tdStyle}><button style={btnSmall} onClick={() => openEditor(p)}>Edit Preset</button></td>
                </tr>
              ))}
              {positions.length === 0 && (
                <tr><td style={tdStyle} colSpan={5}>No HR Positions found for this company.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <div style={overlayStyle}>
          <div style={{ ...modalStyle, width: 720 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
              <div style={{ fontSize: 16, fontWeight: 700 }}>Permission Preset — {editing.title}</div>
              <div style={{ fontSize: 12, color: '#6b7280' }}>{checked.size} selected</div>
            </div>
            {catalog.map((mod) => (
              <div key={mod.module_key} style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#1a1a2e', marginBottom: 6 }}>{mod.module_name}</div>
                {mod.submodules.map((sub) => (
                  <div key={sub.submodule_name} style={{ marginBottom: 8, marginLeft: 8 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>{sub.submodule_name}</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      {sub.permissions.map((perm) => (
                        <label key={perm.id} style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 4, background: checked.has(perm.id) ? '#eff6ff' : '#f9fafb', padding: '3px 8px', borderRadius: 6, border: '1px solid #e5e7eb' }}>
                          <input type="checkbox" checked={checked.has(perm.id)} onChange={() => toggle(perm.id)} />
                          {perm.name}
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ))}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12, position: 'sticky', bottom: 0, background: '#fff', paddingTop: 8 }}>
              <button style={btnSecondary} onClick={() => setEditing(null)}>Cancel</button>
              <button style={btnPrimary} onClick={savePreset} disabled={saving}>{saving ? 'Saving...' : 'Save Preset'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
